import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { UpperbeltPdfViewerComponent } from "../upperbelt-pdf-viewer/upperbelt-pdf-viewer.component";
import {
  UpperbeltActivity,
  UpperbeltFileCategory,
  UpperbeltUploadKind,
} from "../../models";
import { UpperbeltFilesService } from "../../services/upperbelt-files.service";

type UploadState = "idle" | "uploading" | "success" | "error";

interface UpperbeltFilters {
  type: string;
  courseName: string;
  activitySequence: string;
  name: string;
}

@Component({
  selector: "app-upperbelt-files",
  templateUrl: "./upperbelt-files.component.html",
  styleUrls: ["./upperbelt-files.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class UpperbeltFilesComponent implements OnInit {
  @HostBinding("class") class = "app-upperbelt-files";

  activities: UpperbeltActivity[] = [];
  filteredActivities: UpperbeltActivity[] = [];
  filters: UpperbeltFilters = {
    type: "",
    courseName: "",
    activitySequence: "",
    name: "",
  };
  isLoading = false;
  uploadStates: { [key: string]: UploadState } = {};

  constructor(
    private readonly upperbeltFilesService: UpperbeltFilesService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.upperbeltFilesService.getActivities().subscribe(
      (activities) => {
        this.activities = activities;
        this.applyFilters();
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
        this.snackBar.open("Unable to load upperbelt activities", "Dismiss", {
          duration: 5000,
        });
      }
    );
  }

  get types(): string[] {
    return this.getUniqueValues((activity) => activity.type);
  }

  get courseNames(): string[] {
    return this.getUniqueValues((activity) => activity.courseName);
  }

  get activitySequences(): number[] {
    return this.getUniqueValues((activity) => activity.activitySequence).sort(
      (a, b) => a - b
    );
  }

  applyFilters(): void {
    const nameFilter = this.filters.name.trim().toLowerCase();

    this.filteredActivities = this.activities.filter((activity) => {
      return (
        (!this.filters.type || activity.type === this.filters.type) &&
        (!this.filters.courseName ||
          activity.courseName === this.filters.courseName) &&
        (!this.filters.activitySequence ||
          activity.activitySequence.toString() ===
            this.filters.activitySequence) &&
        (!nameFilter || activity.name.toLowerCase().includes(nameFilter))
      );
    });
  }

  clearFilters(): void {
    this.filters = {
      type: "",
      courseName: "",
      activitySequence: "",
      name: "",
    };
    this.applyFilters();
  }

  onFileSelected(
    event: Event,
    activity: UpperbeltActivity,
    uploadKind: UpperbeltUploadKind,
    fileCategory: UpperbeltFileCategory
  ): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    input.value = "";

    if (!file) return;

    if (!this.isAllowedFile(file, uploadKind)) {
      this.snackBar.open(
        uploadKind === "pdf"
          ? "Please select a PDF file"
          : "Please select an image file",
        "Dismiss",
        { duration: 4000 }
      );
      return;
    }

    const uploadKey = this.getUploadKey(activity, uploadKind, fileCategory);
    this.uploadStates[uploadKey] = "uploading";

    this.upperbeltFilesService
      .uploadFile(activity, uploadKind, fileCategory, file)
      .subscribe(
        () => {
          this.uploadStates[uploadKey] = "success";
          this.snackBar.open(
            `${this.getUploadFileName(activity, uploadKind, file)} uploaded`,
            "Dismiss",
            { duration: 3000 }
          );
        },
        () => {
          this.uploadStates[uploadKey] = "error";
          this.snackBar.open("Upload failed", "Dismiss", { duration: 5000 });
        }
      );
  }

  getUploadState(
    activity: UpperbeltActivity,
    uploadKind: UpperbeltUploadKind,
    fileCategory: UpperbeltFileCategory
  ): UploadState {
    return (
      this.uploadStates[
        this.getUploadKey(activity, uploadKind, fileCategory)
      ] || "idle"
    );
  }

  getUploadLabel(
    activity: UpperbeltActivity,
    uploadKind: UpperbeltUploadKind,
    fileCategory: UpperbeltFileCategory
  ): string {
    const state = this.getUploadState(activity, uploadKind, fileCategory);
    if (state === "uploading") return "Uploading";
    if (state === "success") return "Uploaded";
    if (state === "error") return "Retry";
    return uploadKind === "pdf" ? "Upload PDF" : "Upload Image";
  }

  viewPdf(
    activity: UpperbeltActivity,
    fileCategory: UpperbeltFileCategory
  ): void {
    this.upperbeltFilesService.getPdf(activity, fileCategory).subscribe(
      (pdf) => {
        const dialogRef = this.dialog.open(UpperbeltPdfViewerComponent, {
          data: {
            name: activity.name,
            pdfBlob: pdf,
          },
          width: "85vw",
          maxWidth: "85vw",
          panelClass: "upperbelt-pdf-dialog",
          autoFocus: false,
        });
      },
      () => {
        this.snackBar.open("Unable to load PDF", "Dismiss", {
          duration: 5000,
        });
      }
    );
  }

  viewImage(activity: UpperbeltActivity): void {
    this.upperbeltFilesService.getImage(activity).subscribe(
      (image) => {
        const objectUrl = URL.createObjectURL(image);
        window.open(objectUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      },
      () => {
        this.snackBar.open("Unable to load image", "Dismiss", {
          duration: 5000,
        });
      }
    );
  }

  trackByActivityId(_: number, activity: UpperbeltActivity): string {
    return activity.id;
  }

  getActivitySequenceLabel(activity: UpperbeltActivity): string {
    return `${activity.activitySequence}`;
  }

  private getUploadFileName(
    activity: UpperbeltActivity,
    uploadKind: UpperbeltUploadKind,
    file: File
  ): string {
    const extension =
      uploadKind === "pdf" ? "pdf" : this.getImageExtension(file);
    return `${activity.id}.${extension}`;
  }

  private isAllowedFile(file: File, uploadKind: UpperbeltUploadKind): boolean {
    const fileName = file.name.toLowerCase();

    if (uploadKind === "pdf") {
      return file.type === "application/pdf" || fileName.endsWith(".pdf");
    }

    return file.type.startsWith("image/");
  }

  private getUploadKey(
    activity: UpperbeltActivity,
    uploadKind: UpperbeltUploadKind,
    fileCategory: UpperbeltFileCategory
  ): string {
    return `${fileCategory}-${activity.id}-${uploadKind}`;
  }

  private getImageExtension(file: File): string {
    const fileExtension = file.name.split(".").pop();
    const mimeExtension = file.type.split("/").pop();
    const extension = fileExtension || mimeExtension || "image";

    return extension.replace(/[^a-z0-9]/gi, "").toLowerCase();
  }

  private getUniqueValues<T>(
    selector: (activity: UpperbeltActivity) => T
  ): T[] {
    return Array.from(new Set(this.activities.map(selector))).sort();
  }
}
